import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Spinner, Table } from "flowbite-react";
import axios from "axios";
import { HiSearch } from "react-icons/hi";
import { QUERIES } from "../config/apiConfig";
import axiosInstance from "../config/axiosInstance";
import { formatDocument } from "../utils/utils";

const SelectUF = ({ label, name, register }) => {
  const estados = [
    "",
    "AC",
    "AL",
    "AP",
    "AM",
    "BA",
    "CE",
    "DF",
    "ES",
    "GO",
    "MA",
    "MT",
    "MS",
    "MG",
    "PA",
    "PB",
    "PR",
    "PE",
    "PI",
    "RJ",
    "RN",
    "RS",
    "RO",
    "RR",
    "SC",
    "SP",
    "SE",
    "TO",
  ];

  return (
    <div className="flex flex-col">
      <label className="text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        {...register(name)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500"
      >
        {estados.map((uf) => (
          <option key={uf} value={uf}>
            {uf === "" ? "Selecione um estado" : uf}
          </option>
        ))}
      </select>
    </div>
  );
};

const LocalizaEmpresaPage = () => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [erro, setErro] = useState("");
  const [buscaRealizada, setBuscaRealizada] = useState(false);

  const onSubmit = async (data) => {
    const algumPreenchido = Object.values(data).some(
      (val) => val && val.trim() !== ""
    );
    if (!algumPreenchido) {
      setError("form", {
        type: "manual",
        message: "Preencha pelo menos um campo para buscar.",
      });
      return;
    }

    clearErrors("form");
    setLoading(true);
    setErro("");
    setResultados([]);

    const queryParams = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value && value.trim() !== "") queryParams.append(key, value.trim());
    });

    try {
      const response = await axiosInstance.post(QUERIES.LOCALIZE_PJ, data); // rota local

      setResultados(response.data.empresaLista || []);
      setBuscaRealizada(true);
    } catch (err) {
      console.error(err);
      setErro(
        err.response?.data?.error || "Erro ao buscar dados. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto mt-2 p-4 bg-white rounded-2xl shadow-lg">
      <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-2">
        <HiSearch className="text-blue-800" /> Localizador de Empresas
      </h1>
      <p className="mb-6">
        Preencha <span className="font-semibold">um ou mais campos</span> abaixo
        para localizar empresas de forma rápida e segura.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {errors.form && (
          <p className="text-red-600 font-semibold">{errors.form.message}</p>
        )}
        {erro && <p className="text-red-600 font-semibold">{erro}</p>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-gray-300 border p-3 bg-gray-50 rounded-md">
          <Input label="Razão Social" name="Razao" register={register} />
          <Input label="DDD" name="ddd" register={register} maxLength={2} />
          <Input label="Telefone" name="fone" register={register} />
          <Input label="CNAE" name="Cnae" register={register} />
          <Input
            label="Natureza Jurídica"
            name="NatJuridica"
            register={register}
          />
          <SelectUF label="UF" name="UF" register={register} />

          <Input label="Cidade" name="Cidade" register={register} />
        </div>

        <div className="pt-4 flex justify-center">
          <button
            type="submit"
            className="primary text-white px-10 py-3 rounded-xl font-medium shadow disabled:opacity-60"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <Spinner size="sm" />
                Buscando...
              </div>
            ) : (
              "Buscar"
            )}
          </button>
        </div>
      </form>

      {/* Resultados */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
          <span className="ml-3 text-gray-700">Carregando resultados...</span>
        </div>
      )}

      {!loading && resultados.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-semibold text-gray-700 mb-3">
            Resultados encontrados: {resultados.length}
          </h2>

          <div className="overflow-x-auto border rounded-xl shadow">
            <Table striped>
              <Table.Head>
                <Table.HeadCell>Razão Social</Table.HeadCell>
                <Table.HeadCell>CNPJ</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {resultados.map((empresa, i) => (
                  <Table.Row key={i} className="bg-white">
                    <Table.Cell>{empresa.razao || "-"}</Table.Cell>
                    <Table.Cell>
                      {formatDocument(empresa.cnpj) || "-"}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </div>
      )}

      {!loading && resultados.length === 0 && buscaRealizada && (
        <p
          className="text-center text-gray-500 mt-8 flex items-center justify-center bg-gray-50 font-semibold border border-gray-300"
          style={{ height: "250px" }}
        >
          Nenhum resultado encontrado para os dados informados.
        </p>
      )}
    </div>
  );
};

const Input = ({ label, name, register, type = "text", maxLength }) => (
  <div className="flex flex-col">
    <label className="text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      type={type}
      {...register(name)}
      maxLength={maxLength}
      className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500"
    />
  </div>
);

export default LocalizaEmpresaPage;
